import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isImpersonating, setIsImpersonating] = useState(!!localStorage.getItem('admin_token'));
  const [impersonatedUser, setImpersonatedUser] = useState(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      if (localStorage.getItem('admin_token')) {
        setIsImpersonating(true);
        setImpersonatedUser(response.data);
      } else {
        setIsImpersonating(false);
        setImpersonatedUser(null);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, businessName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      business_name: businessName
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const startImpersonation = useCallback((accessToken, userData) => {
    // Save current admin token
    const currentToken = localStorage.getItem('token');
    localStorage.setItem('admin_token', currentToken);
    // Set impersonated user token
    localStorage.setItem('token', accessToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setToken(accessToken);
    setUser(userData);
    setIsImpersonating(true);
    setImpersonatedUser(userData);
  }, []);

  const stopImpersonation = useCallback(() => {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      localStorage.removeItem('admin_token');
      localStorage.setItem('token', adminToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
      setToken(adminToken);
      setIsImpersonating(false);
      setImpersonatedUser(null);
      // fetchUser will be triggered by token change via useEffect
    }
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setIsImpersonating(false);
    setImpersonatedUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, token,
      isImpersonating, impersonatedUser,
      startImpersonation, stopImpersonation
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
